import { useEffect, useRef, useState } from 'react';
import { useViewerBinaryCache } from './useViewerBinaryCache';

function createIdleState() {
    return {
        error: null,
        resource: null,
        status: 'idle',
    };
}

function createLoadingState() {
    return {
        error: null,
        resource: null,
        status: 'loading',
    };
}

export function useCachedBinaryResource({
    consumerId,
    enabled = true,
    type,
    url,
} = {}) {
    const viewerBinaryCache = useViewerBinaryCache();
    const [resourceState, setResourceState] = useState(createIdleState);
    const requestTokenRef = useRef(0);
    const resolvedConsumerId = consumerId || (type && url ? `${type}:${url}` : null);

    useEffect(() => {
        requestTokenRef.current += 1;
        const requestToken = requestTokenRef.current;

        if (!enabled || !type || !url) {
            setResourceState(createIdleState());
            return undefined;
        }

        setResourceState(createLoadingState());

        const acquiredResource = viewerBinaryCache.acquire({
            consumerId: resolvedConsumerId,
            type,
            url,
        });

        acquiredResource.promise
            .then((resource) => {
                if (requestTokenRef.current !== requestToken) {
                    return;
                }

                setResourceState({
                    error: null,
                    resource,
                    status: 'ready',
                });
            })
            .catch((error) => {
                if (viewerBinaryCache.isAbortError(error)) {
                    return;
                }

                if (requestTokenRef.current !== requestToken) {
                    return;
                }

                setResourceState({
                    error,
                    resource: null,
                    status: 'failed',
                });
            });

        return () => {
            acquiredResource.release();
        };
    }, [enabled, resolvedConsumerId, type, url, viewerBinaryCache]);

    const invalidate = () => {
        if (!type || !url) {
            return false;
        }

        return viewerBinaryCache.invalidate({
            type,
            url,
        });
    };

    return {
        ...resourceState,
        invalidate,
    };
}
